import {
    Cancel as CancelIcon,
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import {
    Alert,
    alpha,
    Box,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { ApplicationButton, LocalizedTextField, UploadAvatarDialog as UploadAvatarDialogJS } from '../../../../MicrospecDynamicUI/Components';
import { useOrganizationContext } from '../../../../MicrospecDynamicUI/Dashboard/OrganizationContext';
import { useMicrospecDynamicUIContext } from '../../../../MicrospecDynamicUI/GlobalProvider';
import { getDataFromCollectionWithQuery, getDocById, saveDocument } from '../../../../MicrospecDynamicUI/Helpers/dataUtils';
import { BusinessCategory } from '../../../../MicrospecDynamicUI/Models/Business/BusinessCategory';
import { BusinessProduct } from '../../../../MicrospecDynamicUI/Models/Business/BusinessProduct';
import { BusinessSpecification } from '../../../../MicrospecDynamicUI/Models/Business/BusinessSpecification';
import { BusinessSpecificationCategory } from '../../../../MicrospecDynamicUI/Models/Business/BusinessSpecificationCategory';
import { useMicrospecThemeLanguageContext } from '../../../../MicrospecDynamicUI/Themes';
import { BRANCH_ID, CATEGORIES_COLLECTION, ORG_ID, PRODUCTS_COLLECTION, SPECIFICATION_CATEGORIES_COLLECTION, SPECIFICATIONS_COLLECTION } from '../../../constants/collectionPaths';

const UploadAvatarDialog = UploadAvatarDialogJS as any;

interface UpsertProductProps {
  productId?: string;
  onSave?: (product: BusinessProduct) => void;
  onCancel?: () => void;
}

const UpsertProduct: React.FC<UpsertProductProps> = ({ productId, onSave, onCancel }) => {
  const { localizationProvider } = useMicrospecThemeLanguageContext();
  const { translate } = localizationProvider;
  const theme = useTheme();
  const { usedLocalization } = useOrganizationContext();
  const organizationId = ORG_ID;
  const branchId = BRANCH_ID;
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { firestore } = cloudProvider;
  const db = firestore?.instance;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [categories, setCategories] = useState<BusinessCategory[]>([]);
  const [specCategories, setSpecCategories] = useState<BusinessSpecificationCategory[]>([]);
  const [specifications, setSpecifications] = useState<BusinessSpecification[]>([]);
  const [expandedSpecCats, setExpandedSpecCats] = useState<Record<string, boolean>>({});

  const tempIdRef = useRef<string>(`temp_${Date.now()}`);
  const storageId = productId || tempIdRef.current;

  const isMultiLocale = usedLocalization?.supported?.length > 1;

  const [formData, setFormData] = useState<BusinessProduct>({
    name: isMultiLocale ? {} : '',
    category_id: '',
    price: 0,
    quantity_label: isMultiLocale ? {} : '',
    description: isMultiLocale ? {} : '',
    images: [],
    specification_ids: [],
    is_published: true,
    is_available: true,
  });

  // Load categories for dropdown
  useEffect(() => {
    const fetchCategories = async () => {
      if (!db) return;
      try {
        const data = await getDataFromCollectionWithQuery(null, null, db, CATEGORIES_COLLECTION, [], true, 200);
        setCategories(data as BusinessCategory[]);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, [db]);

  // Load spec categories and specifications
  useEffect(() => {
    const fetchSpecs = async () => {
      if (!db) return;
      try {
        const [cats, specs] = await Promise.all([
          getDataFromCollectionWithQuery(null, null, db, SPECIFICATION_CATEGORIES_COLLECTION, [], true, 200),
          getDataFromCollectionWithQuery(null, null, db, SPECIFICATIONS_COLLECTION, [], true, 500),
        ]);
        setSpecCategories(cats as BusinessSpecificationCategory[]);
        setSpecifications(specs as BusinessSpecification[]);
        // Expand all spec categories by default
        const expanded: Record<string, boolean> = {};
        (cats as BusinessSpecificationCategory[]).forEach(c => { if (c.id) expanded[c.id] = true; });
        setExpandedSpecCats(expanded);
      } catch (err) {
        console.error('Error fetching specifications:', err);
      }
    };
    fetchSpecs();
  }, [db]);

  // Load existing product if editing
  useEffect(() => {
    const loadProduct = async () => {
      if (!productId || !db) return;
      setLoading(true);
      try {
        const data = await getDocById(null, null, db, PRODUCTS_COLLECTION, productId, false);
        if (data) {
          setFormData({
            name: data.name || '',
            category_id: data.category_id || '',
            price: data.price || 0,
            quantity_label: data.quantity_label || (isMultiLocale ? {} : ''),
            description: data.description || '',
            images: data.images || [],
            specification_ids: data.specification_ids || [],
            is_published: data.is_published ?? true,
            is_available: data.is_available ?? true,
            created_at: data.created_at,
            updated_at: data.updated_at,
          });
        }
      } catch (err) {
        console.error('Error loading product:', err);
        setError(translate('product.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };
    loadProduct();
  }, [productId, db, translate, isMultiLocale]);

  const handleInputChange = (field: keyof BusinessProduct, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleImageUploaded = (url: string) => {
    setFormData(prev => ({ ...prev, images: [...(prev.images || []), url] }));
    setUploadDialogOpen(false);
  };

  const handleRemoveImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index),
    }));
  };

  const handleToggleSpecification = (specId: string) => {
    setFormData(prev => {
      const current = prev.specification_ids || [];
      const next = current.includes(specId)
        ? current.filter(id => id !== specId)
        : [...current, specId];
      return { ...prev, specification_ids: next };
    });
  };

  const validateForm = (): boolean => {
    if (!formData.category_id) {
      setError(translate('product.categoryRequired'));
      return false;
    }
    const name = formData.name;
    if (typeof name === 'string' && !name.trim()) {
      setError(translate('product.nameRequired'));
      return false;
    }
    if (typeof name === 'object' && !Object.values(name).some(v => v.trim())) {
      setError(translate('product.nameRequired'));
      return false;
    }
    if (formData.price < 0) {
      setError(translate('product.priceInvalid'));
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    if (!db || !organizationId || !branchId) {
      setError(translate('product.missingContext'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const productData: BusinessProduct = {
        ...formData,
        created_at: productId ? formData.created_at : new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      const result = await saveDocument(firestore, PRODUCTS_COLLECTION, productData, { id: productId });
      setSuccess(true);
      if (onSave) {
        onSave({ ...productData, id: result.id });
      } else {
        if (!productId) {
          setFormData({
            name: '',
            category_id: '',
            price: 0,
            quantity_label: '',
            description: '',
            images: [],
            specification_ids: [],
            is_published: true,
            is_available: true,
          });
        }
        setSuccess(false);
      }
    } catch (err: any) {
      setError(err.message || translate('product.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      setFormData({ name: '', category_id: '', price: 0, quantity_label: '', description: '', images: [], specification_ids: [], is_published: true, is_available: true });
      setError(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const newImageStorageRef = `organizations/${organizationId}/branches/${branchId}/products/${storageId}/images/${Date.now()}.jpg`;

  return (
    <>
      <Card sx={{ borderRadius: 3, boxShadow: theme.palette.mode === 'dark' ? `0 8px 32px ${alpha(theme.palette.common.black, 0.4)}` : `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}` }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3, color: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[900] }}>
            {productId ? translate('product.editTitle') : translate('product.createTitle')}
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 3 }}>{translate('product.savedSuccessfully')}</Alert>}

          <Grid container spacing={3}>
            {/* Name */}
            <Grid item xs={12} md={6}>
              {usedLocalization?.supported?.length > 1 ? (
                <LocalizedTextField
                  languageConfig={usedLocalization}
                  value={typeof formData.name === 'object' ? formData.name : { [usedLocalization.preferred]: formData.name || '' }}
                  onChange={(value: any) => handleInputChange('name', value)}
                  label={translate('product.name')}
                  required
                  helperText={translate('product.enterName')}
                  variant="outlined"
                />
              ) : (
                <TextField
                  fullWidth
                  label={translate('product.name')}
                  value={typeof formData.name === 'string' ? formData.name : (formData.name as any)?.[usedLocalization?.preferred || 'en-US'] || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                  helperText={translate('product.enterName')}
                  variant="outlined"
                />
              )}
            </Grid>

            {/* Category */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>{translate('product.category')}</InputLabel>
                <Select
                  value={formData.category_id}
                  onChange={(e) => handleInputChange('category_id', e.target.value)}
                  label={translate('product.category')}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {translate(cat.name)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Price */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={translate('product.price')}
                type="number"
                value={formData.price}
                onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                helperText={translate('product.priceHelperText')}
                variant="outlined"
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            {/* Quantity Label */}
            <Grid item xs={12} md={4}>
              {isMultiLocale ? (
                <LocalizedTextField
                  languageConfig={usedLocalization}
                  value={typeof formData.quantity_label === 'object' ? formData.quantity_label! : { [usedLocalization.preferred]: (formData.quantity_label as string) || '' }}
                  onChange={(value: any) => handleInputChange('quantity_label', value)}
                  label={translate('product.quantityLabel')}
                  helperText={translate('product.quantityLabelHelperText')}
                  variant="outlined"
                />
              ) : (
                <TextField
                  fullWidth
                  label={translate('product.quantityLabel')}
                  value={typeof formData.quantity_label === 'string' ? formData.quantity_label : (formData.quantity_label as any)?.[usedLocalization?.preferred || 'en-US'] || ''}
                  onChange={(e) => handleInputChange('quantity_label', e.target.value)}
                  helperText={translate('product.quantityLabelHelperText')}
                  variant="outlined"
                />
              )}
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              {usedLocalization?.supported?.length > 1 ? (
                <LocalizedTextField
                  languageConfig={usedLocalization}
                  value={typeof formData.description === 'object' ? formData.description : { [usedLocalization.preferred]: (formData.description as string) || '' }}
                  onChange={(value: any) => handleInputChange('description', value)}
                  label={translate('product.description')}
                  multiline
                  rows={3}
                  helperText={translate('product.enterDescription')}
                  variant="outlined"
                />
              ) : (
                <TextField
                  fullWidth
                  label={translate('product.description')}
                  value={typeof formData.description === 'string' ? formData.description : (formData.description as any)?.[usedLocalization?.preferred || 'en-US'] || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  multiline
                  rows={3}
                  helperText={translate('product.enterDescription')}
                  variant="outlined"
                />
              )}
            </Grid>

            {/* Images */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {translate('product.images')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                {(formData.images || []).map((url, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: 'relative',
                      width: 140,
                      height: 100,
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    }}
                  >
                    <Box component="img" src={url} alt={`product-${index}`} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <Tooltip title={translate('common.delete')}>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveImage(index)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          backgroundColor: alpha(theme.palette.error.main, 0.85),
                          color: '#fff',
                          '&:hover': { backgroundColor: theme.palette.error.main },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
                <Box
                  sx={{
                    width: 140,
                    height: 100,
                    borderRadius: 2,
                    border: `2px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    gap: 0.5,
                    color: alpha(theme.palette.primary.main, 0.7),
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
                  }}
                  onClick={() => setUploadDialogOpen(true)}
                >
                  <CloudUploadIcon />
                  <Typography variant="caption" fontWeight={500}>{translate('product.addImage')}</Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary">{translate('product.imagesHelperText')}</Typography>
            </Grid>

            {/* Specifications */}
            {specCategories.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {translate('product.specifications')}
                </Typography>
                <Box sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 2, overflow: 'hidden' }}>
                  {specCategories.map((cat, catIdx) => {
                    const catSpecs = specifications.filter(s => s.specification_category_id === cat.id && s.is_available);
                    if (catSpecs.length === 0) return null;
                    const isExpanded = expandedSpecCats[cat.id!] ?? true;
                    const selectedInCat = catSpecs.filter(s => (formData.specification_ids || []).includes(s.id!));
                    return (
                      <Box key={cat.id}>
                        {catIdx > 0 && <Divider />}
                        {/* Category header */}
                        <Box
                          sx={{
                            px: 2, py: 1.5,
                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
                          }}
                          onClick={() => setExpandedSpecCats(prev => ({ ...prev, [cat.id!]: !isExpanded }))}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700}>{translate(cat.name)}</Typography>
                            {selectedInCat.length > 0 && (
                              <Chip label={selectedInCat.length} size="small" color="primary" />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">{isExpanded ? '▲' : '▼'}</Typography>
                        </Box>
                        {/* Specs list */}
                        <Collapse in={isExpanded}>
                          <Box sx={{ px: 2, py: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {catSpecs.map(spec => {
                              const checked = (formData.specification_ids || []).includes(spec.id!);
                              return (
                                <FormControlLabel
                                  key={spec.id}
                                  control={
                                    <Checkbox
                                      checked={checked}
                                      onChange={() => handleToggleSpecification(spec.id!)}
                                      size="small"
                                      color="primary"
                                    />
                                  }
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography variant="body2">{translate(spec.name)}</Typography>
                                      {spec.additional_price > 0 && (
                                        <Typography variant="caption" color="text.secondary">(+₪{spec.additional_price.toFixed(2)})</Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ mr: 1 }}
                                />
                              );
                            })}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
                {(formData.specification_ids || []).length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {(formData.specification_ids || []).length} {translate('product.specificationsSelected')}
                  </Typography>
                )}
              </Grid>
            )}

            {/* Published / Available */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Switch checked={formData.is_published} onChange={(e) => handleInputChange('is_published', e.target.checked)} color="primary" />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>{translate('product.published')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formData.is_published ? translate('product.visibleToUsers') : translate('product.hiddenFromUsers')}
                    </Typography>
                  </Box>
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Switch checked={formData.is_available} onChange={(e) => handleInputChange('is_available', e.target.checked)} color="success" />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>{translate('product.available')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formData.is_available ? translate('product.inStock') : translate('product.outOfStock')}
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            {/* Actions */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <ApplicationButton variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel} disabled={saving} size="large">
                  {translate('common.cancel')}
                </ApplicationButton>
                <ApplicationButton
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                  size="large"
                  sx={{
                    minWidth: 150,
                    background: theme.palette.mode === 'dark'
                      ? `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`
                      : `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                    boxShadow: `0 3px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': { boxShadow: `0 5px 20px ${alpha(theme.palette.primary.main, 0.4)}` },
                  }}
                >
                  {saving ? translate('common.loading') : translate('common.save')}
                </ApplicationButton>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <UploadAvatarDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onImageUploaded={handleImageUploaded}
        currentImageUrl={null}
        storageRef={newImageStorageRef}
        aspectRatio={4 / 3}
        cropShape="rect"
        maxWidth={800}
        quality={0.85}
      />
    </>
  );
};

export default UpsertProduct;
